/**
 * ConfigPage — Admin configuration dashboard for Eternity portal configs.
 *
 * Provides a password-gated UI for visually tweaking each portal's
 * halo position, portrait framing, effect presets, and identity fields.
 * All changes persist to localStorage via the config-store module.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  loadConfigs,
  saveConfigs,
  isAuthenticated,
  authenticate,
  PUBLISH_PASSWORD,
  type PortalConfigData,
} from '../lib/config-store';
import { HALO_CONFIGS } from '../engine/halo-configs';
import { PORTAL_PRESETS } from '../effects/presets';
import { GuillocheHalo } from '../components/GuillocheHalo';

// ── Design Tokens ──

const GOLD = '#C5A258';
const BG_PAGE = '#0A0A0A';
const BG_PANEL = '#111';
const TEXT_PRIMARY = '#F5F0E8';
const TEXT_SECONDARY = 'rgba(245,240,232,0.5)';
const FONT_HEADING = "'Cormorant Garamond', Georgia, serif";
const FONT_BODY = "'Noto Serif', Georgia, serif";
const ASPECT_RATIO = 1.45;

// ── Slider Sub-component ──

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  const decimals = step < 1 ? 2 : 0;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '0.375rem',
      }}
    >
      <span
        style={{
          width: 140,
          flexShrink: 0,
          fontSize: '0.8125rem',
          fontFamily: FONT_BODY,
          color: TEXT_SECONDARY,
        }}
      >
        {label}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ flex: 1, accentColor: GOLD }}
      />
      <span
        style={{
          width: 50,
          textAlign: 'right',
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          color: TEXT_PRIMARY,
        }}
      >
        {value.toFixed(decimals)}
      </span>
    </div>
  );
}

// ── Section Header ──

function SectionLabel({ children }: { children: string }) {
  return (
    <h4
      style={{
        fontFamily: FONT_HEADING,
        fontSize: '0.9375rem',
        fontWeight: 600,
        color: GOLD,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        margin: '1rem 0 0.5rem',
        borderBottom: `1px solid rgba(197,162,88,0.2)`,
        paddingBottom: '0.25rem',
      }}
    >
      {children}
    </h4>
  );
}

// ── Shared Input Styles ──

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.625rem',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(197,162,88,0.25)',
  borderRadius: 4,
  color: TEXT_PRIMARY,
  fontFamily: FONT_BODY,
  fontSize: '0.875rem',
  outline: 'none',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
};

const buttonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  background: 'rgba(197,162,88,0.12)',
  border: `1px solid rgba(197,162,88,0.4)`,
  borderRadius: 4,
  color: GOLD,
  fontFamily: FONT_HEADING,
  fontSize: '0.875rem',
  letterSpacing: '0.06em',
  cursor: 'pointer',
};

// ── Interaction Modes ──

type InteractionMode = 'halo' | 'leftEye' | 'rightEye';

// ── Default Config Factory ──

function createDefaultPortal(): PortalConfigData {
  const id = `portal-${Date.now()}`;
  return {
    id,
    name: 'New Portal',
    subtitle: 'Subtitle',
    imageSrc: '',
    pinealGland: { x: 0.5, y: 0.35 },
    haloTranslate: { x: 0, y: 0 },
    haloScale: 1.0,
    imageScale: 0.75,
    imageOffsetX: 0,
    fadeStart: 0.65,
    archPointedness: 0.22,
    archStart: 0.33,
    frameThickness: 14,
    eyePositions: null,
    haloConfigKey: Object.keys(HALO_CONFIGS)[0] ?? 'makarios',
    portalPresetKey: Object.keys(PORTAL_PRESETS)[0] ?? 'makarios',
  };
}

// ── Preview Component ──

function PortalPreview({
  portal,
  mode,
  onModeChange,
  onPortalChange,
}: {
  portal: PortalConfigData;
  mode: InteractionMode;
  onModeChange: (m: InteractionMode) => void;
  onPortalChange: (p: PortalConfigData) => void;
}) {
  const previewRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ mx: 0, my: 0, tx: 0, ty: 0 });

  const previewWidth = 340;
  const previewHeight = previewWidth * ASPECT_RATIO;

  // Halo position computation
  const haloCx = portal.pinealGland.x * previewWidth + portal.haloTranslate.x;
  const haloCy = portal.pinealGland.y * previewHeight + portal.haloTranslate.y;
  const haloSize = previewWidth * portal.haloScale;
  const haloConfig = HALO_CONFIGS[portal.haloConfigKey] ?? HALO_CONFIGS[Object.keys(HALO_CONFIGS)[0]];

  // Drag handlers (window-level for smooth out-of-bounds dragging)
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (mode !== 'halo') {
        // Eye placement
        const rect = previewRef.current?.getBoundingClientRect();
        if (!rect) return;
        const nx = (e.clientX - rect.left) / rect.width;
        const ny = (e.clientY - rect.top) / rect.height;
        const clamped = { x: Math.max(0, Math.min(1, nx)), y: Math.max(0, Math.min(1, ny)) };

        if (mode === 'leftEye') {
          const existing = portal.eyePositions ?? { left: { x: 0, y: 0 }, right: { x: 0, y: 0 } };
          onPortalChange({ ...portal, eyePositions: { ...existing, left: clamped } });
          onModeChange('rightEye');
        } else if (mode === 'rightEye') {
          const existing = portal.eyePositions ?? { left: { x: 0, y: 0 }, right: { x: 0, y: 0 } };
          onPortalChange({ ...portal, eyePositions: { ...existing, right: clamped } });
          onModeChange('halo');
        }
        return;
      }

      // Start halo drag
      draggingRef.current = true;
      dragStartRef.current = {
        mx: e.clientX,
        my: e.clientY,
        tx: portal.haloTranslate.x,
        ty: portal.haloTranslate.y,
      };
    },
    [mode, portal, onPortalChange, onModeChange],
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      const dx = e.clientX - dragStartRef.current.mx;
      const dy = e.clientY - dragStartRef.current.my;
      onPortalChange({
        ...portal,
        haloTranslate: {
          x: Math.round(dragStartRef.current.tx + dx),
          y: Math.round(dragStartRef.current.ty + dy),
        },
      });
    };

    const onMouseUp = () => {
      draggingRef.current = false;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [portal, onPortalChange]);

  const imageWidth = previewWidth * portal.imageScale;

  return (
    <div style={{ width: previewWidth, flexShrink: 0 }}>
      {/* Preview Area */}
      <div
        ref={previewRef}
        onMouseDown={handleMouseDown}
        style={{
          position: 'relative',
          width: previewWidth,
          height: previewHeight,
          background: BG_PAGE,
          border: `1px solid rgba(197,162,88,0.3)`,
          borderRadius: 4,
          overflow: 'hidden',
          cursor: mode === 'halo' ? 'grab' : 'crosshair',
          userSelect: 'none',
        }}
      >
        {/* Portrait Image */}
        {portal.imageSrc && (
          <img
            src={portal.imageSrc}
            alt={portal.name}
            draggable={false}
            style={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: `translateX(calc(-50% + ${portal.imageOffsetX * previewWidth * 0.5}px))`,
              width: imageWidth,
              height: 'auto',
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Guilloche Halo Overlay */}
        <GuillocheHalo
          config={haloConfig}
          size={haloSize}
          style={{
            position: 'absolute',
            left: haloCx,
            top: haloCy,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          }}
        />

        {/* Halo center indicator */}
        <div
          style={{
            position: 'absolute',
            left: haloCx - 4,
            top: haloCy - 4,
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: GOLD,
            opacity: 0.9,
            pointerEvents: 'none',
            boxShadow: `0 0 6px ${GOLD}`,
          }}
        />

        {/* Eye markers */}
        {portal.eyePositions?.left && (
          <div
            style={{
              position: 'absolute',
              left: portal.eyePositions.left.x * previewWidth - 5,
              top: portal.eyePositions.left.y * previewHeight - 5,
              width: 10,
              height: 10,
              borderRadius: '50%',
              border: '2px solid #44aaff',
              pointerEvents: 'none',
            }}
          />
        )}
        {portal.eyePositions?.right && (
          <div
            style={{
              position: 'absolute',
              left: portal.eyePositions.right.x * previewWidth - 5,
              top: portal.eyePositions.right.y * previewHeight - 5,
              width: 10,
              height: 10,
              borderRadius: '50%',
              border: '2px solid #44aaff',
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Bottom fade gradient */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: `${(1 - portal.fadeStart) * 100}%`,
            background: `linear-gradient(to bottom, transparent, ${BG_PAGE})`,
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* Mode Toggle Buttons */}
      <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem' }}>
        {(['halo', 'leftEye', 'rightEye'] as InteractionMode[]).map((m) => {
          const labels: Record<InteractionMode, string> = {
            halo: 'Drag Halo',
            leftEye: 'Set Left Eye',
            rightEye: 'Set Right Eye',
          };
          const active = mode === m;
          return (
            <button
              key={m}
              onClick={() => onModeChange(m)}
              style={{
                ...buttonStyle,
                flex: 1,
                fontSize: '0.75rem',
                padding: '0.375rem 0.5rem',
                background: active ? 'rgba(197,162,88,0.25)' : 'rgba(197,162,88,0.08)',
                borderColor: active ? GOLD : 'rgba(197,162,88,0.25)',
              }}
            >
              {labels[m]}
            </button>
          );
        })}
      </div>

      {/* Image Upload */}
      <label
        style={{
          display: 'block',
          marginTop: '0.5rem',
          padding: '0.5rem',
          textAlign: 'center',
          background: 'rgba(255,255,255,0.04)',
          border: '1px dashed rgba(197,162,88,0.3)',
          borderRadius: 4,
          color: TEXT_SECONDARY,
          fontFamily: FONT_BODY,
          fontSize: '0.8125rem',
          cursor: 'pointer',
        }}
      >
        Upload Image
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => {
              if (typeof reader.result === 'string') {
                onPortalChange({ ...portal, imageSrc: reader.result });
              }
            };
            reader.readAsDataURL(file);
          }}
        />
      </label>
    </div>
  );
}

// ── Controls Panel ──

function ControlsPanel({
  portal,
  onChange,
}: {
  portal: PortalConfigData;
  onChange: (p: PortalConfigData) => void;
}) {
  const update = useCallback(
    <K extends keyof PortalConfigData>(key: K, val: PortalConfigData[K]) => {
      onChange({ ...portal, [key]: val });
    },
    [portal, onChange],
  );

  const updateNested = useCallback(
    (
      parent: 'pinealGland' | 'haloTranslate',
      axis: 'x' | 'y',
      val: number,
    ) => {
      onChange({ ...portal, [parent]: { ...portal[parent], [axis]: val } });
    },
    [portal, onChange],
  );

  return (
    <div
      style={{
        flex: 1,
        minWidth: 300,
        maxHeight: `${340 * ASPECT_RATIO + 110}px`,
        overflowY: 'auto',
        paddingRight: '0.5rem',
      }}
    >
      {/* Position */}
      <SectionLabel>Position</SectionLabel>
      <Slider
        label="Halo Translate X"
        value={portal.haloTranslate.x}
        min={-200}
        max={200}
        step={1}
        onChange={(v) => updateNested('haloTranslate', 'x', v)}
      />
      <Slider
        label="Halo Translate Y"
        value={portal.haloTranslate.y}
        min={-200}
        max={200}
        step={1}
        onChange={(v) => updateNested('haloTranslate', 'y', v)}
      />
      <Slider
        label="Pineal X"
        value={portal.pinealGland.x}
        min={0}
        max={1}
        step={0.01}
        onChange={(v) => updateNested('pinealGland', 'x', v)}
      />
      <Slider
        label="Pineal Y"
        value={portal.pinealGland.y}
        min={0}
        max={1}
        step={0.01}
        onChange={(v) => updateNested('pinealGland', 'y', v)}
      />

      {/* Halo */}
      <SectionLabel>Halo</SectionLabel>
      <Slider
        label="Scale"
        value={portal.haloScale}
        min={0.3}
        max={2}
        step={0.05}
        onChange={(v) => update('haloScale', v)}
      />

      {/* Portrait */}
      <SectionLabel>Portrait</SectionLabel>
      <Slider
        label="Image Scale"
        value={portal.imageScale}
        min={0.3}
        max={2}
        step={0.05}
        onChange={(v) => update('imageScale', v)}
      />
      <Slider
        label="Image Offset X"
        value={portal.imageOffsetX}
        min={-1}
        max={1}
        step={0.01}
        onChange={(v) => update('imageOffsetX', v)}
      />
      <Slider
        label="Fade Start"
        value={portal.fadeStart}
        min={0.3}
        max={1}
        step={0.01}
        onChange={(v) => update('fadeStart', v)}
      />

      {/* Frame */}
      <SectionLabel>Frame</SectionLabel>
      <Slider
        label="Arch Pointedness"
        value={portal.archPointedness}
        min={0}
        max={0.5}
        step={0.01}
        onChange={(v) => update('archPointedness', v)}
      />
      <Slider
        label="Arch Start"
        value={portal.archStart}
        min={0.1}
        max={0.6}
        step={0.01}
        onChange={(v) => update('archStart', v)}
      />
      <Slider
        label="Frame Thickness"
        value={portal.frameThickness}
        min={4}
        max={30}
        step={1}
        onChange={(v) => update('frameThickness', v)}
      />

      {/* Presets */}
      <SectionLabel>Presets</SectionLabel>
      <div style={{ marginBottom: '0.375rem' }}>
        <label
          style={{
            display: 'block',
            fontSize: '0.8125rem',
            fontFamily: FONT_BODY,
            color: TEXT_SECONDARY,
            marginBottom: '0.25rem',
          }}
        >
          Halo Config
        </label>
        <select
          value={portal.haloConfigKey}
          onChange={(e) => update('haloConfigKey', e.target.value)}
          style={selectStyle}
        >
          {Object.keys(HALO_CONFIGS).map((key) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
        </select>
      </div>
      <div style={{ marginBottom: '0.375rem' }}>
        <label
          style={{
            display: 'block',
            fontSize: '0.8125rem',
            fontFamily: FONT_BODY,
            color: TEXT_SECONDARY,
            marginBottom: '0.25rem',
          }}
        >
          Portal Effect
        </label>
        <select
          value={portal.portalPresetKey}
          onChange={(e) => update('portalPresetKey', e.target.value)}
          style={selectStyle}
        >
          {Object.keys(PORTAL_PRESETS).map((key) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
        </select>
      </div>

      {/* Identity */}
      <SectionLabel>Identity</SectionLabel>
      <div style={{ marginBottom: '0.375rem' }}>
        <label
          style={{
            display: 'block',
            fontSize: '0.8125rem',
            fontFamily: FONT_BODY,
            color: TEXT_SECONDARY,
            marginBottom: '0.25rem',
          }}
        >
          Name
        </label>
        <input
          type="text"
          value={portal.name}
          onChange={(e) => update('name', e.target.value)}
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: '0.375rem' }}>
        <label
          style={{
            display: 'block',
            fontSize: '0.8125rem',
            fontFamily: FONT_BODY,
            color: TEXT_SECONDARY,
            marginBottom: '0.25rem',
          }}
        >
          Subtitle
        </label>
        <input
          type="text"
          value={portal.subtitle}
          onChange={(e) => update('subtitle', e.target.value)}
          style={inputStyle}
        />
      </div>
    </div>
  );
}

// ── Portal Editor Modal ──

function PortalEditor({
  portal,
  onClose,
  onSave,
}: {
  portal: PortalConfigData;
  onClose: () => void;
  onSave: (p: PortalConfigData) => void;
}) {
  const [draft, setDraft] = useState<PortalConfigData>(portal);
  const [mode, setMode] = useState<InteractionMode>('halo');

  // Sync if parent portal changes (shouldn't normally, but defensive)
  useEffect(() => {
    setDraft(portal);
  }, [portal]);

  const handleChange = useCallback(
    (updated: PortalConfigData) => {
      setDraft(updated);
      onSave(updated);
    },
    [onSave],
  );

  const handleCopyConfig = useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(draft, null, 2)).catch(() => {
      /* clipboard blocked */
    });
  }, [draft]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          maxWidth: 900,
          width: '100%',
          maxHeight: '95vh',
          background: BG_PANEL,
          borderRadius: 8,
          border: `1px solid rgba(197,162,88,0.2)`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.75rem 1.25rem',
            borderBottom: '1px solid rgba(197,162,88,0.15)',
          }}
        >
          <h3
            style={{
              fontFamily: FONT_HEADING,
              fontSize: '1.25rem',
              fontWeight: 500,
              color: GOLD,
              margin: 0,
              letterSpacing: '0.04em',
            }}
          >
            {draft.name}
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button onClick={handleCopyConfig} style={buttonStyle}>
              Copy Config
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: TEXT_SECONDARY,
                fontSize: '1.5rem',
                cursor: 'pointer',
                lineHeight: 1,
                padding: '0.25rem 0.5rem',
              }}
            >
              &times;
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div
          style={{
            display: 'flex',
            gap: '1.5rem',
            padding: '1.25rem',
            overflowY: 'auto',
            flexWrap: 'wrap',
          }}
        >
          {/* Left — Preview */}
          <PortalPreview
            portal={draft}
            mode={mode}
            onModeChange={setMode}
            onPortalChange={handleChange}
          />

          {/* Right — Controls */}
          <ControlsPanel portal={draft} onChange={handleChange} />
        </div>
      </div>
    </div>
  );
}

// ── Password Gate ──

function PasswordGate({ onAuth }: { onAuth: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = useCallback(() => {
    if (authenticate(password)) {
      onAuth();
    } else {
      setError(true);
      setTimeout(() => setError(false), 1500);
    }
  }, [password, onAuth]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: BG_PAGE,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.5rem',
      }}
    >
      <h2
        style={{
          fontFamily: FONT_HEADING,
          fontSize: '1.75rem',
          fontWeight: 400,
          color: GOLD,
          letterSpacing: '0.1em',
          margin: 0,
        }}
      >
        Eternity Config
      </h2>
      <p
        style={{
          fontFamily: FONT_BODY,
          fontSize: '0.9375rem',
          color: TEXT_SECONDARY,
          margin: 0,
        }}
      >
        Enter admin password to continue
      </p>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
          }}
          placeholder="Password"
          autoFocus
          style={{
            ...inputStyle,
            width: 220,
            borderColor: error ? '#c44' : 'rgba(197,162,88,0.25)',
          }}
        />
        <button onClick={handleSubmit} style={buttonStyle}>
          Enter
        </button>
      </div>
      {error && (
        <p
          style={{
            fontFamily: FONT_BODY,
            fontSize: '0.8125rem',
            color: '#c44',
            margin: 0,
          }}
        >
          Invalid password
        </p>
      )}
    </div>
  );
}

// ── Portal Card ──

function PortalCard({
  portal,
  isActive,
  onClick,
  onDelete,
}: {
  portal: PortalConfigData;
  isActive: boolean;
  onClick: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        background: BG_PANEL,
        border: isActive
          ? `2px solid ${GOLD}`
          : '1px solid rgba(197,162,88,0.15)',
        borderRadius: 6,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'border-color 0.2s',
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          aspectRatio: `1 / ${ASPECT_RATIO}`,
          background: BG_PAGE,
          overflow: 'hidden',
        }}
      >
        {portal.imageSrc ? (
          <img
            src={portal.imageSrc}
            alt={portal.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'top',
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(197,162,88,0.3)',
              fontFamily: FONT_HEADING,
              fontSize: '2rem',
            }}
          >
            ?
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '0.625rem 0.75rem' }}>
        <div
          style={{
            fontFamily: FONT_HEADING,
            fontSize: '1rem',
            fontWeight: 500,
            color: TEXT_PRIMARY,
            marginBottom: '0.125rem',
          }}
        >
          {portal.name}
        </div>
        <div
          style={{
            fontFamily: FONT_BODY,
            fontSize: '0.75rem',
            color: TEXT_SECONDARY,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {portal.subtitle}
        </div>
      </div>

      {/* Delete button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        title="Delete portal"
        style={{
          position: 'absolute',
          top: 6,
          right: 6,
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: 'rgba(180,40,40,0.7)',
          border: 'none',
          color: '#fff',
          fontSize: '0.75rem',
          lineHeight: 1,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        &times;
      </button>
    </div>
  );
}

// ── Main ConfigPage ──

export default function ConfigPage() {
  const [authed, setAuthed] = useState(isAuthenticated);
  const [configs, setConfigs] = useState<PortalConfigData[]>(() => loadConfigs());
  const [editingId, setEditingId] = useState<string | null>(null);

  // Persist on every change
  useEffect(() => {
    saveConfigs(configs);
  }, [configs]);

  const editingPortal = editingId
    ? configs.find((c) => c.id === editingId) ?? null
    : null;

  const handleSavePortal = useCallback(
    (updated: PortalConfigData) => {
      setConfigs((prev) =>
        prev.map((c) => (c.id === updated.id ? updated : c)),
      );
    },
    [],
  );

  const handleDeletePortal = useCallback((id: string) => {
    setConfigs((prev) => prev.filter((c) => c.id !== id));
    setEditingId((prev) => (prev === id ? null : prev));
  }, []);

  const handleAddPortal = useCallback(() => {
    const newPortal = createDefaultPortal();
    setConfigs((prev) => [...prev, newPortal]);
    setEditingId(newPortal.id);
  }, []);

  const handleExportAll = useCallback(() => {
    navigator.clipboard
      .writeText(JSON.stringify(configs, null, 2))
      .catch(() => {
        /* clipboard blocked */
      });
  }, [configs]);

  const [publishing, setPublishing] = useState(false);
  const [publishStatus, setPublishStatus] = useState<string | null>(null);

  const handlePublish = useCallback(async () => {
    setPublishing(true);
    setPublishStatus(null);
    try {
      const res = await fetch('/api/publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: PUBLISH_PASSWORD, configs }),
      });
      const data = await res.json();
      if (data.ok) {
        setPublishStatus(`Published ${data.portals} portals`);
      } else {
        setPublishStatus(`Error: ${data.error}`);
      }
    } catch {
      setPublishStatus('Publish server unreachable');
    } finally {
      setPublishing(false);
    }
  }, [configs]);

  // Password gate
  if (!authed) {
    return <PasswordGate onAuth={() => setAuthed(true)} />;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: BG_PAGE,
        color: TEXT_PRIMARY,
        padding: '2rem',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <h1
          style={{
            fontFamily: FONT_HEADING,
            fontSize: '1.75rem',
            fontWeight: 400,
            color: GOLD,
            letterSpacing: '0.08em',
            margin: 0,
          }}
        >
          Eternity Config
        </h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={handlePublish} disabled={publishing} style={{
            ...buttonStyle,
            background: publishing ? 'rgba(197,162,88,0.1)' : 'rgba(80,200,120,0.15)',
            borderColor: publishing ? 'rgba(197,162,88,0.2)' : 'rgba(80,200,120,0.4)',
            color: publishing ? TEXT_SECONDARY : '#50C878',
          }}>
            {publishing ? 'Publishing…' : 'Publish'}
          </button>
          {publishStatus && (
            <span
              style={{
                fontFamily: FONT_BODY,
                fontSize: '0.75rem',
                alignSelf: 'center',
                color: publishStatus.startsWith('Published') ? '#50C878' : '#ff6b6b',
              }}
            >
              {publishStatus}
            </span>
          )}
          <button onClick={handleAddPortal} style={buttonStyle}>
            + Add Portal
          </button>
          <button onClick={handleExportAll} style={buttonStyle}>
            Export All
          </button>
        </div>
      </div>

      {/* Card Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '1.25rem',
        }}
      >
        {configs.map((portal) => (
          <PortalCard
            key={portal.id}
            portal={portal}
            isActive={editingId === portal.id}
            onClick={() => setEditingId(portal.id)}
            onDelete={() => handleDeletePortal(portal.id)}
          />
        ))}
      </div>

      {/* Editor Modal */}
      {editingPortal && (
        <PortalEditor
          portal={editingPortal}
          onClose={() => setEditingId(null)}
          onSave={handleSavePortal}
        />
      )}
    </div>
  );
}
